import { Divider } from '@/components/Divider';
import { cn } from '@/lib/cn';
import { AccordionMultipleProps } from '@radix-ui/react-accordion';
import { number, type StreamDataColumn } from '@squaredup/data-streams';
import Field from 'components/forms/field/Field';
import { defaultWrapperClassName } from 'components/forms/input/Input';
import { VisualisationOptionAction } from 'dashboard-engine/types/Visualisation';
import { isEmpty } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { VisualisationConfigAccordion } from '../VisualisationConfigAccordion';
import { DataStreamGaugeConfig } from './Config';

type Option = { label: string; value: string };

type GroupedOption = {
    label: string;
    options: Option[];
};

interface DataStreamGaugeChartOptionsFormProps {
    config: DataStreamGaugeConfig;
    accordionControls: AccordionMultipleProps;
    onChange: (action: VisualisationOptionAction) => void;
    columns: StreamDataColumn[];
}

const FIXED_OPTION: Option = { label: 'Fixed', value: '__fixed__' };
const DEFAULT_MIN = 0;
const DEFAULT_MAX = 100;

export const getValidColumns = (columns: StreamDataColumn[]): StreamDataColumn[] => {
    const valueColumns = columns.filter((c) => c.role === 'value');
    const numberColumns = columns.filter((c) => c.valueShapeName === number.name && c.role !== 'value');
    return [...valueColumns, ...numberColumns];
};

function DataStreamGaugeChartOptionsForm({
    config,
    accordionControls,
    onChange,
    columns
}: DataStreamGaugeChartOptionsFormProps) {
    const columnOptions = getValidColumns(columns).map((column) => ({
        label: column.displayName,
        value: column.name
    }));

    const groupedColumnOptions = useMemo<GroupedOption[]>(
        () => [
            { label: '', options: [FIXED_OPTION] },
            {
                label: 'Dynamic',
                options: columnOptions
            }
        ],
        [columnOptions]
    );

    const minimumColumn = columnOptions.find((c) => c.value === config?.minimumColumn);
    const maximumColumn = columnOptions.find((c) => c.value === config?.maximumColumn);

    const defaultValues: DataStreamGaugeConfig = {
        label: config?.label ?? '',
        minimum: config?.minimum ?? DEFAULT_MIN,
        maximum: config?.maximum ?? DEFAULT_MAX,
        minimumColumn: minimumColumn?.value ?? FIXED_OPTION.value,
        maximumColumn: maximumColumn?.value ?? FIXED_OPTION.value
    };

    const form = useForm<DataStreamGaugeConfig>({
        mode: 'onChange',
        defaultValues
    });

    // Reset form if config changes
    useEffect(() => {
        if (isEmpty(config)) {
            form.reset(defaultValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config]);

    // Notify parent on form changes
    useEffect(() => {
        const subscription = form.watch((data) => onChange({ action: 'form-change', data }));
        return () => subscription.unsubscribe();
    }, [form, onChange]);

    // Set min/max to undefined when a column is selected, and vice versa
    const handleColumnChange = (type: 'minimum' | 'maximum', option: Option | null) => {
        const selected = option ?? FIXED_OPTION;
        if (selected.value === FIXED_OPTION.value) {
            // Fixed selected: clear column, keep value
            form.setValue(`${type}Column` as const, FIXED_OPTION.value);
            form.setValue(type, form.getValues(type) ?? (type === 'minimum' ? DEFAULT_MIN : DEFAULT_MAX));
        } else {
            // Column selected: set column, clear value
            form.setValue(`${type}Column` as const, selected.value);
            form.setValue(type, undefined);
        }
    };

    return (
        <FormProvider {...form}>
            <form className='w-full' onSubmit={form.handleSubmit((data) => onChange({ action: 'form-change', data }))}>
                <VisualisationConfigAccordion value='range' label='Range' accordionControls={accordionControls}>
                    <div className='flex flex-col items-start gap-y-4'>
                        <div className='flex items-center gap-x-2 mt-2 w-full'>
                            <div
                                className={cn(
                                    form.getValues('minimumColumn') === FIXED_OPTION.value ? 'w-1/2' : 'w-full'
                                )}
                            >
                                <Controller
                                    name='minimumColumn'
                                    control={form.control}
                                    render={({ field }) => (
                                        <Field.Input
                                            {...field}
                                            type='autocomplete'
                                            isMulti={false}
                                            label='Min'
                                            fieldClassName='w-full'
                                            options={groupedColumnOptions}
                                            value={
                                                groupedColumnOptions.reduce<Option | undefined>(
                                                    (found, group) =>
                                                        found ||
                                                        group.options.find((option) => option.value === field.value),
                                                    undefined
                                                ) || FIXED_OPTION
                                            }
                                            onChange={(option: Option) => handleColumnChange('minimum', option)}
                                            isClearable={false}
                                            getOptionLabel={(option: Option) => option.label}
                                            getOptionValue={(option: Option) => option.value}
                                        />
                                    )}
                                />
                            </div>
                            {form.getValues('minimumColumn') === FIXED_OPTION.value && (
                                <div className='w-1/2'>
                                    <Controller
                                        name='minimum'
                                        control={form.control}
                                        render={({ field }) => (
                                            <input
                                                type='number'
                                                className={cn(defaultWrapperClassName, 'w-full mt-7')}
                                                {...field}
                                                onChange={(e) =>
                                                    field.onChange(
                                                        isNaN(parseInt(e.target.value))
                                                            ? DEFAULT_MIN
                                                            : parseInt(e.target.value)
                                                    )
                                                }
                                                placeholder={String(DEFAULT_MIN)}
                                            />
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                        <div className='flex items-center gap-x-2 w-full'>
                            <div
                                className={cn(
                                    form.getValues('maximumColumn') === FIXED_OPTION.value ? 'w-1/2' : 'w-full'
                                )}
                            >
                                <Controller
                                    name='maximumColumn'
                                    control={form.control}
                                    render={({ field }) => (
                                        <Field.Input
                                            {...field}
                                            type='autocomplete'
                                            label='Max'
                                            isMulti={false}
                                            fieldClassName='w-full'
                                            options={groupedColumnOptions}
                                            value={
                                                groupedColumnOptions.reduce<Option | undefined>(
                                                    (found, group) =>
                                                        found ||
                                                        group.options.find((option) => option.value === field.value),
                                                    undefined
                                                ) || FIXED_OPTION
                                            }
                                            onChange={(option: Option) => handleColumnChange('maximum', option)}
                                            isClearable={false}
                                            getOptionLabel={(option: Option) => option.label}
                                            getOptionValue={(option: Option) => option.value}
                                        />
                                    )}
                                />
                            </div>
                            {form.getValues('maximumColumn') === FIXED_OPTION.value && (
                                <div className='w-1/2'>
                                    <Controller
                                        name='maximum'
                                        control={form.control}
                                        render={({ field }) => (
                                            <input
                                                type='number'
                                                className={cn(defaultWrapperClassName, 'w-full mt-7')}
                                                {...field}
                                                onChange={(e) =>
                                                    field.onChange(
                                                        isNaN(parseInt(e.target.value))
                                                            ? DEFAULT_MAX
                                                            : parseInt(e.target.value)
                                                    )
                                                }
                                                placeholder={String(DEFAULT_MAX)}
                                            />
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </VisualisationConfigAccordion>

                <Divider />

                <VisualisationConfigAccordion
                    value='monitoring'
                    label='Monitoring'
                    accordionControls={accordionControls}
                >
                    <p className='text-sm text-textSecondary'>
                        To monitor this value with thresholds configure Monitoring on this tile using the tab above.
                    </p>
                </VisualisationConfigAccordion>

                <Divider />

                <VisualisationConfigAccordion value='options' label='Options' accordionControls={accordionControls}>
                    <Field label='Label'>
                        <Controller
                            name='label'
                            control={form.control}
                            render={({ field }) => (
                                <input
                                    type='text'
                                    className={defaultWrapperClassName}
                                    {...field}
                                    maxLength={250}
                                    placeholder='Enter a label'
                                />
                            )}
                        />
                    </Field>
                </VisualisationConfigAccordion>
            </form>
        </FormProvider>
    );
}

export default DataStreamGaugeChartOptionsForm;
